/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from 'react';
import type { HTMLAttributes } from 'react';
import {
  DataGrid,
  GridColType,
  gridClasses,
  GridRowId,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { IconButton, Tooltip } from '@mui/material';
import Icon from '@mui/material/Icon';
import type { TSchema } from 'types/items';
import type { Configuration } from '../../Schema/schema.types';

// Resolve the selection model to the concrete row ids that are currently
// loaded. MUI X v7 selection is `{ type: 'include' | 'exclude', ids }`, and
// "Select all" emits an `exclude` model (often with an empty `ids` set) meaning
// "everything except these". `allIds` are the ids of the rows on the current
// (server-paginated) page, so we intersect/subtract against those.
function gridRowSelectionIds(
  model: GridRowSelectionModel,
  allIds: GridRowId[]
): GridRowId[] {
  if (model.type === 'exclude') {
    return allIds.filter((id) => !model.ids.has(id));
  }
  return allIds.filter((id) => model.ids.has(id));
}

interface Props extends HTMLAttributes<HTMLDivElement> {
  configuration: Configuration;
  disablePagination: boolean;
  data: any;
  gridHeightCss?: string;
  loading: boolean;
  onButtonClick?: (buttonName: string, row: any) => void;
  paginationModel: any;
  onRowClick: (row: any) => void;
  rowCount: number;
  rowSelectionModel: GridRowSelectionModel;
  schema: TSchema<any>;
  setPaginationModel: (paginationModel: any) => void;
  setRowsSelected: (rowsSelected: boolean) => void;
  setRowSelectionModel: (rowSelectionModel: GridRowSelectionModel) => void;
  setSelectedRows: (selectedRows: any[]) => void;
  setSortModel: (sortModel: any) => void;
  sortModel: any;
}

export default function DataTable({
  configuration,
  data,
  disablePagination,
  gridHeightCss,
  loading,
  onButtonClick,
  paginationModel,
  onRowClick,
  rowCount,
  rowSelectionModel,
  schema,
  setPaginationModel,
  setRowsSelected,
  setRowSelectionModel,
  setSelectedRows,
  setSortModel,
  sortModel,
}: Props) {
  // Hooks
  const columns = useMemo(() => {
    if (configuration.listViewColumns && schema?.fields) {
      return configuration.listViewColumns.map((column) => {
        // Handle custom action columns
        if ((column as any).type === 'iconbutton') {
          return {
            ...column,
            headerName:
              (column as any).headerName !== undefined
                ? (column as any).headerName
                : column.field,
            width: 60,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params: any) => (
              <Tooltip title={(column as any).label || 'Action'}>
                <span>
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation(); // Prevent row click event
                      onButtonClick?.(
                        (column as any).buttonName || column.field,
                        params.row
                      );
                    }}
                    size="small"
                    disabled={(column as any).disabled === true}
                  >
                    <Icon>
                      {typeof (column as any).icon === 'function'
                        ? (column as any).icon(params.row)
                        : (column as any).icon}
                    </Icon>
                  </IconButton>
                </span>
              </Tooltip>
            ),
          };
        }

        // Handle regular field columns
        return {
          ...column,
          headerName:
            column.headerName ||
            schema.fields[column.field]?.label ||
            column.field,
          type:
            schema.fields[column.field]?.type === 'bool'
              ? 'boolean'
              : (schema.fields[column.field]?.type as GridColType),
          valueFormatter:
            column.valueFormatter ||
            ((params: string) => {
              let resp = params;
              if (schema.fields[column.field]?.type === 'datetime' && params) {
                resp = new Date(params).toLocaleString();
              } else if (schema.fields[column.field]?.type === 'enum') {
                // If the column type is an enum, we want to return the valueLabel of the enum.
                // To determine this, you need to find the index of the column value within the
                // values array and then return the same indexed value from the valueLabels array.

                const index =
                  schema.fields[column.field]?.values?.indexOf(params);
                if (index !== undefined) {
                  resp =
                    schema.fields[column.field]?.valueLabels?.[index] || params;
                }
              }

              return resp;
            }),
        };
      });
    }
    return [];
  }, [configuration.listViewColumns, schema, onButtonClick]);

  return (
    <div style={{ width: '100%', paddingLeft: 10 }}>
      <DataGrid
        autoPageSize
        checkboxSelection={!configuration.checkboxSelectionDisabled}
        columns={columns}
        disableColumnSelector
        disableColumnFilter
        disableRowSelectionOnClick={true}
        getRowId={configuration.getRowId || ((row) => row.id)}
        hideFooter={disablePagination}
        loading={loading}
        paginationMode="server"
        paginationModel={paginationModel}
        rowCount={rowCount}
        rows={data}
        rowSelectionModel={rowSelectionModel}
        sortingMode="server"
        sortModel={sortModel}
        onPaginationModelChange={(newPaginationModel) => {
          setPaginationModel(newPaginationModel);
        }}
        onRowClick={onRowClick}
        onRowSelectionModelChange={(m) => {
          setRowSelectionModel(m);
          const getRowIdFn = configuration.getRowId || ((row) => row.id);
          const allIds: GridRowId[] = data.map((r: any) => getRowIdFn(r));
          const ids = gridRowSelectionIds(m, allIds);
          setRowsSelected(ids.length > 0);
          setSelectedRows(
            ids.map((rowId) => data.find((r: any) => getRowIdFn(r) === rowId))
          );
        }}
        onSortModelChange={(newSortModel) => {
          if (paginationModel) {
            setSortModel(newSortModel);
          }
        }}
        sx={{
          borderTop: 0,
          borderLeft: 0,
          borderRight: 0,
          height: gridHeightCss || 'calc(100vh - 150px)',
          [`& .${gridClasses.columnHeaders}`]: {
            paddingLeft: '0px',
            paddingRight: `1px`,
          },
          [`& .${gridClasses.columnHeaderTitle}`]: {
            textTransform: 'uppercase',
            color: 'rgb(123, 128, 154)',
            fontSize: '0.65rem',
            fontWeight: 700,
          },
          // Additional button spacing optimizations to prevent horizontal scroll
          [`& .${gridClasses.cell}`]: {
            '&[data-field$="Actions"]': {
              maxWidth: '120px !important',
              minWidth: '80px !important',
              padding: '4px 8px !important',
            },
            '&[data-field*="button"]': {
              maxWidth: '120px !important',
              minWidth: '80px !important',
              padding: '4px 8px !important',
            },
            '&[data-field*="icon"]': {
              maxWidth: '60px !important',
              minWidth: '48px !important',
              padding: '4px 8px !important',
              textAlign: 'center',
            },
          },
          // Optimize button elements within cells
          '& .MuiButton-root': {
            minWidth: '70px !important',
            padding: '4px 8px !important',
            margin: '0 2px !important',
            fontSize: '0.75rem',
          },
          '& .MuiIconButton-root': {
            padding: '4px !important',
            margin: '0 1px !important',
          },
          // Reduce overall column width for better space utilization
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
        }}
      />
    </div>
  );
}
